import { useState, useEffect, useCallback } from 'react';
import { rpc } from '@/api/client';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { Pagination } from '@/components/common/Pagination';
import { FilterBar } from '@shared/search';
import { driversListSchema } from './driversListSchema';
import { Users, Flag, Calendar } from 'lucide-react';
import type { ListRaceDriversResponse } from '@/types/telemetry';

export function DriversList() {
  const { params, navigate } = useRouter();
  const { t } = useI18n();

  const page = parseInt(params.page || '1', 10);
  const query = params.q || '';

  const [data, setData] = useState<ListRaceDriversResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (currentPage: number, currentQuery: string) => {
    setLoading(true);
    setError(null);
    try {
      const rpcBody: Record<string, unknown> = { page: currentPage, pageSize: 10 };
      if (currentQuery) rpcBody.query = currentQuery;

      const res = await rpc<ListRaceDriversResponse>('ListRaceDrivers', rpcBody);
      setData(res);
    } catch (e: any) {
      setError(e.message || t('drivers.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData(page, query);
  }, [page, query, loadData]);

  const handleSearch = (newQuery: string) => {
    const qp = new URLSearchParams();
    qp.set('page', '1');
    if (newQuery) qp.set('q', newQuery);
    navigate(`#/drivers?${qp.toString()}`);
  };

  const handlePageChange = useCallback((newPage: number) => {
    const qp = new URLSearchParams();
    qp.set('page', String(newPage));
    if (query) qp.set('q', query);
    navigate(`#/drivers?${qp.toString()}`);
  }, [query, navigate]);

  const totalPages = data?.totalCount ? Math.ceil(data.totalCount / 10) : 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-9 h-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
          <Users size={18} className="text-accent" />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('drivers.title')}</h1>
          <p className="text-xs text-text-muted mt-0.5">{t('nav.drivers')}</p>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="mb-6">
        <FilterBar
          schema={driversListSchema}
          initialQuery={query}
          onSearch={handleSearch}
          enableFilterUI={true}
        />
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState message={error} />
      ) : !data || data.drivers.length === 0 ? (
        <ErrorState message={t('drivers.noData')} />
      ) : (
        <>
          <div className="card overflow-hidden">
            {/* Desktop table */}
            <table className="w-full border-collapse hidden sm:table">
              <thead>
                <tr className="divider">
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('drivers.driver')}
                  </th>
                  <th className="w-[180px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('drivers.lastAppearance')}
                  </th>
                  <th className="w-[100px] px-4 py-2.5 text-right text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('drivers.events')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.drivers.map((driver, index) => (
                  <tr
                    key={driver.id}
                    onClick={() => navigate(`#/races/driver/${driver.id}`)}
                    className={`hover:bg-bg-hover transition-colors cursor-pointer ${index < data.drivers.length - 1 ? 'divider' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <span className="font-semibold text-text-primary text-sm">
                        {driver.firstName} {driver.lastName}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-text-muted font-medium">
                        {driver.lastSeasonYear > 0 && driver.lastCategoryName ? (
                          `${driver.lastCategoryName} ${driver.lastSeasonYear}`
                        ) : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-text-secondary text-sm tabular-nums mono">
                        {driver.raceCount}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile list */}
            <div className="sm:hidden">
              {data.drivers.map((driver, index) => (
                <div
                  key={driver.id}
                  onClick={() => navigate(`#/races/driver/${driver.id}`)}
                  className={`p-4 hover:bg-bg-hover transition-colors cursor-pointer ${index < data.drivers.length - 1 ? 'divider' : ''}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-text-primary text-sm truncate">
                        {driver.firstName} {driver.lastName}
                      </div>
                      {driver.lastSeasonYear > 0 && driver.lastCategoryName && (
                        <div className="text-xs text-text-muted mt-0.5 font-medium">
                          {driver.lastCategoryName} '{String(driver.lastSeasonYear).slice(-2)}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-text-muted shrink-0 tabular-nums mono">
                      <Calendar size={12} />
                      {driver.raceCount}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {totalPages > 1 && (
            <div className="mt-8">
              <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
